/**
 * WooCommerce – obsługa przycisków ilości produktów w koszyku
 *
 * Ulepszenia względem oryginału:
 *  - Debounce na aktualizację koszyka (zapobiega spamowaniu requestami)
 *  - Usunięcie pętli: listener 'change' na inpucie NIE wywołuje już wc_fragment_refresh
 *    (żeby uniknąć: click → dispatchEvent('change') → fragment_refresh → re-render → click...)
 *  - Walidacja wartości wejściowej (NaN, poza zakresem)
 *  - Pojedyncza instancja MutationObserver (bez ryzyka duplikacji)
 *  - Czytelna architektura: każda funkcja ma jedno zadanie
 */

// ─── Konfiguracja ────────────────────────────────────────────────────────────

const QTY_CONFIG = {
    debounceMs: 300,       // Opóźnienie przed wysłaniem aktualizacji koszyka
    defaultMin: 1,
    defaultMax: 9999,
};

// ─── Pomocnicze ──────────────────────────────────────────────────────────────

/**
 * Tworzy funkcję z debounce – wywołuje `fn` dopiero po `delay` ms od ostatniego wywołania.
 */
const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn(...args), delay);
    };
};

/**
 * Odświeża koszyk WooCommerce przez AJAX.
 * Wywołuje zmianę na inpucie (WooCommerce nasłuchuje tego zdarzenia)
 * oraz ręcznie odświeża fragmenty (mini-cart, licznik produktów itp.)
 */
const triggerCartUpdate = debounce((input) => {
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Fallback – kliknij przycisk aktualizacji jeśli istnieje
    const updateBtn = document.querySelector('button[name="update_cart"]');
    if (updateBtn) updateBtn.click();

    if (typeof jQuery !== 'undefined') {
        jQuery(document.body).trigger('wc_fragment_refresh');
    }
}, QTY_CONFIG.debounceMs);

/**
 * Parsuje wartość do liczby całkowitej; zwraca `fallback` gdy wynik to NaN.
 */
const parseIntSafe = (value, fallback) => {
    const parsed = parseInt(value, 10);
    return isNaN(parsed) ? fallback : parsed;
};

// ─── Inicjalizacja listenerów ─────────────────────────────────────────────────

/**
 * Podpina obsługę przycisków +/− do wszystkich niezainicjowanych `.quantity-wrapper`.
 * Flaga `data-qty-init` zapobiega wielokrotnemu podpinaniu eventów.
 */
const addQtyListeners = () => {
    document.querySelectorAll('.quantity-wrapper:not([data-qty-init="true"])').forEach(wrapper => {
        const btnMinus = wrapper.querySelector('.qty-minus');
        const btnPlus = wrapper.querySelector('.qty-plus');

        // Input może być bezpośrednio w wrapper lub w nadrzędnym .quantity
        const input = wrapper.querySelector('input.qty')
            ?? wrapper.closest('.quantity')?.querySelector('input.qty');

        if (!btnMinus || !btnPlus || !input) return;

        // Oznaczamy wrapper jako zainicjowany
        wrapper.dataset.qtyInit = 'true';

        // Odczytujemy zakres z atrybutów HTML (min/max ustawione przez WooCommerce)
        const getMin = () => parseIntSafe(input.min, QTY_CONFIG.defaultMin);
        const getMax = () => parseIntSafe(input.max, QTY_CONFIG.defaultMax);
        const getVal = () => parseIntSafe(input.value, getMin());

        // Przycisk MINUS
        btnMinus.addEventListener('click', (e) => {
            e.preventDefault();
            const val = getVal();
            const min = getMin();

            if (val > min) {
                input.value = val - 1;
                triggerCartUpdate(input);
            }
        });

        // Przycisk PLUS
        btnPlus.addEventListener('click', (e) => {
            e.preventDefault();
            const val = getVal();
            const max = getMax();

            if (val < max) {
                input.value = val + 1;
                triggerCartUpdate(input);
            }
        });

        // Ręczna edycja pola – walidacja zakresu po wyjściu z pola (blur)
        input.addEventListener('blur', () => {
            const val = getVal();
            const min = getMin();
            const max = getMax();

            // Korygujemy wartość jeśli poza zakresem
            if (val < min) input.value = min;
            if (val > max) input.value = max;

            // Nie wywołujemy tu triggerCartUpdate – WooCommerce samo nasłuchuje 'change'
            // który zostanie wyemitowany przez natywne zdarzenie przeglądarki po blur+change.
        });
    });
};


const fixGtmKitProductData = () => {
    document.querySelectorAll('.remove[data-gtmkit_product_data]').forEach(el => {
        const raw = el.getAttribute('data-gtmkit_product_data');
        if (raw.includes('&quot;')) {
            el.setAttribute('data-gtmkit_product_data', raw.replaceAll('&quot;', '"'));
        }
    });
};


document.addEventListener('DOMContentLoaded', () => {

    addQtyListeners();
    fixGtmKitProductData();

    // Ponowna inicjalizacja po zdarzeniach AJAX WooCommerce
    if (typeof jQuery !== 'undefined') {
        jQuery(document.body).on(
            'updated_cart_totals updated_checkout wc_fragments_refreshed wc_fragments_loaded updated_wc_div',
            () => {
                fixGtmKitProductData(); // Napraw GTM Kit przed ich kodem
                addQtyListeners();
            }
        );
    }

    // MutationObserver – obsługa dynamicznie wstrzykiwanych elementów
    // (np. mini-cart ładowany przez AJAX, one-page checkout itp.)
    const observer = new MutationObserver(() => {
        // Sprawdzamy tylko gdy faktycznie pojawi się nowy, niezainicjowany wrapper
        if (document.querySelector('.quantity-wrapper:not([data-qty-init="true"])')) {
            addQtyListeners();
        }
    });

    observer.observe(document.body, {
        childList: true,
        subtree: true,
    });
});


import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';


document.addEventListener('DOMContentLoaded', () => {
    const swiper = new Swiper('.block-slider .swiper', {
        modules: [Navigation, Pagination, Autoplay],
        slidesPerView: 1,
        spaceBetween: 0,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        resizeObserver: true,
        speed: 1500,
        navigation: {
            nextEl: ".block-slider .swiper-button-next",
            prevEl: ".block-slider .swiper-button-prev",
        },
        autoplay: {
            delay: 5000,
        },
        loop: true,
    });


    var $galleryEl = document.querySelector('.custom-gallery-swiper');

    if ($galleryEl) {
        new Swiper('.custom-gallery-swiper', {
            modules: [Navigation],
            spaceBetween: 10,
            slidesPerView: 4,
            navigation: {
                nextEl: '.custom-gallery-swiper .swiper-button-next',
                prevEl: '.custom-gallery-swiper .swiper-button-prev',
            },
            loop: true,
        });
    }

    // Obsługa wariantów
    var $form = document.querySelector('form.variations_form');
    var $mainWrap = document.querySelector('.custom-main-image');

    if (!$form || !$mainWrap) return;

    var $mainImg = $mainWrap.querySelector('img');
    var originalSrc = $mainImg ? $mainImg.src : '';
    var originalSrcset = $mainImg ? $mainImg.srcset : '';
    var originalAlt = $mainImg ? $mainImg.alt : '';

    jQuery($form).on('found_variation', function (event, variation) {
        if (!variation || !variation.image || !variation.image.src) return;

        if ($mainImg) {
            $mainImg.src = variation.image.src;
            $mainImg.srcset = variation.image.srcset || '';
            $mainImg.alt = variation.image.alt || '';
        }
    });

    jQuery($form).on('reset_image', function () {
        if ($mainImg) {
            $mainImg.src = originalSrc;
            $mainImg.srcset = originalSrcset;
            $mainImg.alt = originalAlt;
        }
    });

});


document.addEventListener('DOMContentLoaded', () => {

    const toggle = () => {
        const show = document.querySelector('[name="custom_checkbox"]:checked')?.value === 'faktura';

        ['custom_nip_field', 'billing_company'].forEach(id => {
            const row = document.getElementById(id + '_field');
            if (row) row.style.display = show ? '' : 'none';
        });
    };

    document.addEventListener('change', e => {
        if (e.target.name === 'custom_checkbox') toggle();
    });

    document.body.addEventListener('updated_checkout', toggle);

    toggle();

    const menuButton = document.querySelector('.menu-mobile');
    const mainMenu = document.querySelector('.header .main-menu');
    const header = document.querySelector('.header');

    if (menuButton && mainMenu) {

        menuButton.addEventListener('click', function () {

            const isActive = mainMenu.classList.contains('active');

            mainMenu.classList.toggle('active');
            menuButton.classList.toggle('is-active');
            document.body.classList.toggle('is-menu-open');

            menuButton.setAttribute('aria-expanded', !isActive);

        });
    }
});

import GLightbox from 'glightbox';
import 'glightbox/dist/css/glightbox.css';

document.addEventListener('DOMContentLoaded', () => {
    const lightbox = GLightbox();
});

document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('dogory')?.addEventListener('click', e => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    });
});